
import React, { useState } from 'react';
import { X, Copy, Check, Link as LinkIcon, ExternalLink } from 'lucide-react';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyUrl: string;
  propertyTitle: string;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, propertyUrl, propertyTitle }) => {
  const [copied, setCopied] = useState(false);
  const [showInstagramTip, setShowInstagramTip] = useState(false);
  const [showTikTokTip, setShowTikTokTip] = useState(false);

  if (!isOpen) return null;

  const encodedUrl = encodeURIComponent(propertyUrl);
  const encodedTitle = encodeURIComponent(`Check out this home on Afriqahome: ${propertyTitle}`);

  const handleCopy = () => {
    navigator.clipboard.writeText(propertyUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSocialClick = (platform: string) => {
    let url = '';

    switch (platform) {
      case 'whatsapp':
        url = `https://wa.me/?text=${encodedTitle} ${encodedUrl}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'instagram':
        // Instagram doesn't have web share API - show instructions
        handleCopy();
        setShowInstagramTip(true);
        return;
      case 'tiktok':
        // TikTok doesn't have web share API - show instructions
        handleCopy();
        setShowTikTokTip(true);
        return;
      default:
        return;
    }

    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-4 sm:p-0">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="bg-[#f7f6f1] w-full max-w-md rounded-t-[2rem] md:rounded-[2rem] shadow-2xl relative z-10 overflow-hidden animate-in slide-in-from-bottom-10 duration-300">
        
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center bg-white/50 backdrop-blur-md">
          <h3 className="text-lg font-bold text-[#134025]">Share this home</h3>
          <button 
            onClick={onClose} 
            className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-300 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Grid */}
        <div className="p-8 grid grid-cols-3 gap-y-8 gap-x-4">
          
          {/* WhatsApp */}
          <ShareItem 
            label="WhatsApp" 
            onClick={() => handleSocialClick('whatsapp')}
            color="bg-[#25D366] text-white"
            icon={
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            }
          />

          {/* Facebook */}
          <ShareItem 
            label="Facebook" 
            onClick={() => handleSocialClick('facebook')}
            color="bg-[#1877F2] text-white"
            icon={
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036c-2.148 0-2.971.956-2.971 3.594v.376h3.428l-.532 3.667h-2.896v7.981a9.986 9.986 0 0 0 8.837-9.836 9.99 9.99 0 0 0-9.965-9.981A9.99 9.99 0 0 0 2.753 13.855 9.986 9.986 0 0 0 9.101 23.691Z"/></svg>
            }
          />

          {/* Instagram */}
          <ShareItem 
            label="Instagram" 
            onClick={() => handleSocialClick('instagram')}
            color="bg-gradient-to-tr from-purple-500 to-pink-500 text-white"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
            }
          />

           {/* LinkedIn */}
           <ShareItem 
            label="LinkedIn" 
            onClick={() => handleSocialClick('linkedin')}
            color="bg-[#0A66C2] text-white"
            icon={
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            }
          />

          {/* TikTok */}
          <ShareItem 
            label="TikTok" 
            onClick={() => handleSocialClick('tiktok')}
            color="bg-black text-white"
            icon={
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.65-1.54-1.15v8.36c0 1.21-.19 2.38-.77 3.45-.63 1.17-1.54 2.15-2.65 2.87-1.66 1.09-3.7 1.64-5.69 1.48-1.92-.16-3.74-.95-5.17-2.28-1.55-1.44-2.43-3.48-2.43-5.59 0-2.11.89-4.15 2.45-5.59 1.49-1.37 3.49-2.12 5.53-2.05.15 0 .3 0 .45.02v4.06c-.17-.03-.35-.04-.52-.04-1.28-.05-2.52.47-3.4 1.41-.9.96-1.37 2.27-1.27 3.58.09 1.23.63 2.38 1.55 3.19.95.83 2.17 1.26 3.4 1.19 1.3-.08 2.51-.62 3.38-1.54.88-.93 1.35-2.21 1.28-3.49V.02h.74z"/></svg>
            }
          />

          {/* Copy Link */}
          <ShareItem 
            label={copied ? "Copied!" : "Copy Link"} 
            onClick={handleCopy}
            color={copied ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-700"}
            icon={copied ? <Check size={24} /> : <LinkIcon size={24} />}
          />

        </div>

        {/* Copy Success Toast */}
        <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 bg-black text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg transition-all duration-300 ${copied && !showInstagramTip && !showTikTokTip ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
           Link Copied!
        </div>

        {/* Instagram Instructions Modal */}
        {showInstagramTip && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full animate-in zoom-in-95 duration-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                </div>
                <h3 className="font-bold text-lg text-gray-900">Share on Instagram</h3>
              </div>
              <ol className="text-sm text-gray-600 space-y-3">
                <li className="flex gap-3 items-start">
                  <span className="bg-[#134025] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
                  <span>Link copied to clipboard <Check size={14} className="inline text-green-600" /></span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="bg-gray-200 text-gray-600 w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
                  <span>Open the Instagram app</span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="bg-gray-200 text-gray-600 w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
                  <span>Create a Story or post</span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="bg-gray-200 text-gray-600 w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">4</span>
                  <span>Use the Link sticker to paste the link</span>
                </li>
              </ol>
              <button
                onClick={() => setShowInstagramTip(false)}
                className="mt-5 w-full py-3 bg-[#134025] text-white rounded-xl font-semibold hover:bg-[#0f3320] transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        )}

        {/* TikTok Instructions Modal */}
        {showTikTokTip && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full animate-in zoom-in-95 duration-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="white" className="w-5 h-5"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.65-1.54-1.15v8.36c0 1.21-.19 2.38-.77 3.45-.63 1.17-1.54 2.15-2.65 2.87-1.66 1.09-3.7 1.64-5.69 1.48-1.92-.16-3.74-.95-5.17-2.28-1.55-1.44-2.43-3.48-2.43-5.59 0-2.11.89-4.15 2.45-5.59 1.49-1.37 3.49-2.12 5.53-2.05.15 0 .3 0 .45.02v4.06c-.17-.03-.35-.04-.52-.04-1.28-.05-2.52.47-3.4 1.41-.9.96-1.37 2.27-1.27 3.58.09 1.23.63 2.38 1.55 3.19.95.83 2.17 1.26 3.4 1.19 1.3-.08 2.51-.62 3.38-1.54.88-.93 1.35-2.21 1.28-3.49V.02h.74z"/></svg>
                </div>
                <h3 className="font-bold text-lg text-gray-900">Share on TikTok</h3>
              </div>
              <ol className="text-sm text-gray-600 space-y-3">
                <li className="flex gap-3 items-start">
                  <span className="bg-[#134025] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
                  <span>Link copied to clipboard <Check size={14} className="inline text-green-600" /></span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="bg-gray-200 text-gray-600 w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
                  <span>Open the TikTok app</span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="bg-gray-200 text-gray-600 w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
                  <span>Create a new video</span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="bg-gray-200 text-gray-600 w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">4</span>
                  <span>Add the link in your bio or video description</span>
                </li>
              </ol>
              <button
                onClick={() => setShowTikTokTip(false)}
                className="mt-5 w-full py-3 bg-black text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

const ShareItem: React.FC<{ label: string; onClick: () => void; color: string; icon: React.ReactNode }> = ({ label, onClick, color, icon }) => (
  <button 
    onClick={onClick}
    className="flex flex-col items-center gap-3 group"
  >
    <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center shadow-sm transition-transform duration-300 group-hover:scale-110 group-active:scale-95 ${color}`}>
      {icon}
    </div>
    <span className="text-xs font-bold text-gray-600 group-hover:text-gray-900 transition-colors">{label}</span>
  </button>
);

export default ShareModal;
